import {
  CommonTrackingData,
  getCommonTrackingData,
  trackingModelIdSplitter,
  VwTrack,
  VwTrackingEvents,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  TrackingEvent,
  TrackingFunctions,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {RangeSimulatorTrackingCtx} from '@volkswagen-onehub/whitelabel-e-mobility-range-feature-app';

const getCommonData = ({
  tenantConfiguration,
  selectedModel,
  selectedTrim,
  formState,
}: RangeSimulatorTrackingCtx): CommonTrackingData => {
  const [trackingModelId, trackingModelTrimFeatures] = trackingModelIdSplitter(
    selectedModel.id
  );

  const selectedRimId = formState.rim;
  const selectedTireType = formState.tireType;

  return getCommonTrackingData(!!tenantConfiguration.global?.commercial, {
    EquipmentlineName: selectedTrim.trimId,
    CarlineId: selectedModel.carlineId,
    CarlineName: selectedModel.carlineName,
    SalesgroupId: selectedModel.salesgroupId,
    SalesgroupName: selectedModel.salesgroupName,
    ModelId: trackingModelId,
    ModelTrimFeatures: trackingModelTrimFeatures,
    ModelName: selectedModel.name,
    EnginePowerHPKW: selectedModel.engine,
    WheelsBasicSize:
      selectedModel.simulationConstants.rims[selectedRimId]?.['size-inch'],
    WheelsBasicType: selectedTireType,
  });
};

export function getRangeTrackingFunctions(
  track: VwTrack
): TrackingFunctions<RangeSimulatorTrackingCtx> {
  return {
    [TrackingEvent.FeatureAppLoaded]: (_, ctx) =>
      track(
        VwTrackingEvents.VWBasic_eMobilityTools_Load,
        getCommonData(ctx),
        undefined
      ),
    [TrackingEvent.DrivingProfileChange]: ({locationPercentages}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'multi_value',
          values: locationPercentages,
          name: 'driving-profile',
        },
      }),
    [TrackingEvent.PayloadToggleChange]: ({value}, ctx) =>
      track(VwTrackingEvents.VWBasic_Checkbox_Click, getCommonData(ctx), {
        link: {
          name: 'payload-people-toggle',
        },
        eventInfo: {
          contentId: value,
        },
      }),
    [TrackingEvent.ExactPayloadChange]: ({payloadKg}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [payloadKg],
          name: 'payload-weight',
        },
      }),
    [TrackingEvent.HighwayToggleChange]: ({value}, ctx) =>
      track(VwTrackingEvents.VWBasic_Checkbox_Click, getCommonData(ctx), {
        link: {
          name: 'highway-toggle',
        },
        eventInfo: {
          contentId: value,
        },
      }),
    [TrackingEvent.HacToggleChange]: ({value}, ctx) =>
      track(VwTrackingEvents.VWBasic_Checkbox_Click, getCommonData(ctx), {
        link: {
          name: 'air-con-toggle',
        },
        eventInfo: {
          contentId: value,
        },
      }),
    [TrackingEvent.EducationalInfoClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.BasicButtonClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_Button_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.RangeTrimSelect]: ({trim, model, tireType, rim}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);
      track(VwTrackingEvents.VWBasic_VehicleSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common data with the new
          // values.
          EquipmentlineName: trim.trimId,
          CarlineId: model.carlineId,
          CarlineName: model.carlineName,
          SalesgroupId: model.salesgroupId,
          SalesgroupName: model.salesgroupName,
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
          WheelsBasicType: tireType,
          WheelsBasicSize: rim['size-inch'],
        },
      });
    },
    [TrackingEvent.RangeEngineSelect]: ({model, tireType, rim}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);

      track(VwTrackingEvents.VWBasic_EngineSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common (old) data with the new
          // values.
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
          WheelsBasicType: tireType,
          WheelsBasicSize: rim['size-inch'],
        },
      });
    },
    [TrackingEvent.RangeTireTypeSelect]: ({tireType, rim}, ctx) =>
      track(VwTrackingEvents.VWBasic_WheelsChange_Click, getCommonData(ctx), {
        configuration: {
          WheelsBasicType: tireType,
          WheelsBasicSize: rim['size-inch'],
        },
      }),
    [TrackingEvent.RangeRimSelect]: ({rim}, ctx) =>
      track(VwTrackingEvents.VWBasic_WheelsChange_Click, getCommonData(ctx), {
        configuration: {
          WheelsBasicSize: rim['size-inch'],
        },
      }),
    [TrackingEvent.OutsideTemperatureChange]: ({temperatureCelsius}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [temperatureCelsius],
          name: 'outside-temperature',
        },
      }),
    [TrackingEvent.ModelEfficiencyDataLayerOpen]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
  };
}
